/**
 * Anwendungslogik des Editors.
 *
 * Der Controller haelt das aktuelle Pattern und die Leadsheet-Metadaten
 * (Titel, Tempo), koordiniert Modell und Speicherung und benachrichtigt
 * Interessenten ueber Aenderungen. Er kennt weder DOM noch konkrete
 * UI-Elemente – dadurch bleibt die Geschaeftslogik aus den Event-Handlern
 * der Oberflaeche heraus.
 */

import { DrumPattern } from '../model/DrumPattern';
import { DEFAULT_META, makeMeta, type LeadsheetMeta } from '../model/leadsheet';
import {
  combineSticking,
  splitSticking,
  type DrumInstrument,
  type StepIndex,
  type Sticking,
  type Hand,
  type Foot,
  type Subdivision,
} from '../model/types';
import {
  loadPattern,
  savePattern,
  clearStoredPattern,
  type PatternStore,
} from '../storage/patternStorage';
import { loadMeta, saveMeta } from '../storage/metaStorage';

/** Unveraenderliche Sicht auf den aktuellen Editor-Zustand. */
export interface EditorSnapshot {
  readonly pattern: DrumPattern;
  readonly meta: LeadsheetMeta;
}

/** Wird nach jeder Zustandsaenderung mit dem aktuellen Zustand aufgerufen. */
export type EditorListener = (snapshot: EditorSnapshot) => void;

export interface EditorControllerOptions {
  /** Optionaler Speicher; standardmaessig wird localStorage genutzt. */
  readonly store?: PatternStore;
  /** Optionales Startpattern; standardmaessig leer. */
  readonly initial?: DrumPattern;
  /** Optionale Start-Metadaten; standardmaessig leer/120 BPM. */
  readonly initialMeta?: LeadsheetMeta;
}

export class EditorController {
  #pattern: DrumPattern;
  #meta: LeadsheetMeta;
  readonly #store: PatternStore | undefined;
  readonly #listeners = new Set<EditorListener>();

  constructor(options: EditorControllerOptions = {}) {
    this.#store = options.store;
    this.#pattern = options.initial ?? DrumPattern.createEmpty();
    this.#meta = options.initialMeta ?? DEFAULT_META;
  }

  /**
   * Erzeugt einen Controller und stellt Pattern und Metadaten aus dem
   * Speicher wieder her. Fehlen oder Beschaedigung fuehren zu leeren
   * bzw. Standardwerten.
   */
  static createFromStorage(store?: PatternStore): EditorController {
    const initial = loadPattern(store) ?? DrumPattern.createEmpty();
    const initialMeta = loadMeta(store);
    return new EditorController(
      store ? { store, initial, initialMeta } : { initial, initialMeta },
    );
  }

  /** Aktuelle Metadaten (Titel, Tempo). */
  get meta(): LeadsheetMeta {
    return this.#meta;
  }

  /** Unveraenderliche Sicht auf den aktuellen Zustand (zum Rendern/Export). */
  snapshot(): EditorSnapshot {
    return { pattern: this.#pattern, meta: this.#meta };
  }

  /** Prueft, ob eine Zelle aktiv ist. */
  isActive(instrument: DrumInstrument, step: StepIndex): boolean {
    return this.#pattern.hasHit(instrument, step);
  }

  /**
   * Schaltet eine Zelle um, speichert und benachrichtigt Interessenten.
   * Gibt zurueck, ob die Zelle danach aktiv ist.
   */
  toggle(instrument: DrumInstrument, step: StepIndex): boolean {
    const active = this.#pattern.toggleHit(instrument, step);
    savePattern(this.#pattern, this.#store);
    this.#notify();
    return active;
  }

  setSubdivision(beat: number | null, subdivision: Subdivision): string | null {
    try {
      this.#pattern.setSubdivision(beat, subdivision);
    } catch (error) {
      return error instanceof Error
        ? error.message
        : 'Rasterwechsel fehlgeschlagen.';
    }
    savePattern(this.#pattern, this.#store);
    this.#notify();
    return null;
  }

  setHand(step: StepIndex, hand: Hand): void {
    const { foot } = splitSticking(this.#pattern.getSticking(step));
    this.setSticking(step, combineSticking(hand, foot));
  }

  setFoot(step: StepIndex, foot: Foot): void {
    const { hand } = splitSticking(this.#pattern.getSticking(step));
    this.setSticking(step, combineSticking(hand, foot));
  }

  setSticking(step: StepIndex, value: Sticking): void {
    this.#pattern.setSticking(step, value);
    savePattern(this.#pattern, this.#store);
    this.#notify();
  }

  /** Leert das Pattern (Titel/Tempo bleiben erhalten). */
  clear(): void {
    this.#pattern.clear();
    clearStoredPattern(this.#store);
    this.#notify();
  }

  /** Setzt den Titel (normalisiert), speichert und benachrichtigt. */
  setTitle(title: string): void {
    this.#meta = makeMeta(title, this.#meta.tempo);
    saveMeta(this.#meta, this.#store);
    this.#notify();
  }

  /** Setzt das Tempo (begrenzt), speichert und benachrichtigt. */
  setTempo(tempo: number): void {
    this.#meta = makeMeta(this.#meta.title, tempo);
    saveMeta(this.#meta, this.#store);
    this.#notify();
  }

  /**
   * Registriert einen Listener und ruft ihn einmalig mit dem aktuellen
   * Zustand auf. Liefert eine Funktion zum Abmelden.
   */
  subscribe(listener: EditorListener): () => void {
    this.#listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.#listeners.delete(listener);
    };
  }

  #notify(): void {
    const snapshot = this.snapshot();
    for (const listener of this.#listeners) {
      listener(snapshot);
    }
  }
}
